import { isAnyOf } from '@reduxjs/toolkit';
import {
  addItem,
  addItemProduct,
  addItemSupply,
  fetchPackingOrder,
  removeItem,
  removeItemSupply,
  requestRelabel,
  saveItem,
  sendToCourier,
} from './operatorPackingThunks';
import { courierWaitEnded, courierWaitRequested, courierWaitStarted } from './operatorPackingActions';
import { LABEL_POLL_MS, awaitingLabel, courierTimeoutSignal, courierWaitSignal } from './operatorPackingUtils';
import { LABEL_WAIT_MS } from '../fulfillment/fulfillmentUtils';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { signalRaised } from '../operator/operatorDeviceSlice';
import { isTaskOnScreen } from '../operator/operatorTaskScope';

/** What answers with an attempt out: a send, the packer's relabel, and the write that finished packing an order whose label went stale. */
const startsAttempt = isAnyOf(
  sendToCourier.fulfilled,
  requestRelabel.fulfilled,
  addItem.fulfilled,
  saveItem.fulfilled,
  addItemProduct.fulfilled,
  removeItem.fulfilled,
  addItemSupply.fulfilled,
  removeItemSupply.fulfilled
);

/**
 * Whether an outcome starts the wait for the labels of the order on screen:
 * its send went through, or creating its shipment again — asked by the
 * packer, or by the server once a write finished packing it —, «Revisar de
 * nuevo» asked for it, or the order opened with an attempt out — once per
 * visit, so the wait's own reads do not start it again. Never while one runs.
 */
const startsLabelWait = (action, state) => {
  const packing = state.operatorPacking;
  if (packing.courierSend.labelWait || !isTaskOnScreen(state, OPERATOR_FLOW.PACKING, packing.orderId)) return false;
  if (startsAttempt(action)) {
    return action.meta.arg.orderId === packing.orderId && awaitingLabel(action.payload);
  }
  if (courierWaitRequested.match(action)) return action.payload === packing.orderId;
  if (fetchPackingOrder.fulfilled.match(action)) {
    return !packing.courierSend.waited && action.meta.arg === packing.orderId && awaitingLabel(packing.order);
  }
  return false;
};

/**
 * Packing's reactions. An order sent to its courier gets its shipment and
 * labels in the background, and so does one whose shipment is created again;
 * there is no socket on the floor: the screen reads the order every
 * `LABEL_POLL_MS` until the attempt is no longer out or the labels are there,
 * the order is left, or `LABEL_WAIT_MS` went by. A read that fails is let go
 * until then. The band says how it ended, unless the order was left.
 *
 * @param {Function} startListening
 */
export const registerOperatorPackingListeners = (startListening) => {
  startListening({
    predicate: startsLabelWait,
    effect: async (action, { dispatch, getState, delay, pause, cancelActiveListeners }) => {
      cancelActiveListeners();
      const { orderId } = getState().operatorPacking;
      const labelWait = { orderId, until: Date.now() + LABEL_WAIT_MS };
      dispatch(courierWaitStarted(labelWait));
      // Asked for again, it reads at once.
      let soon = courierWaitRequested.match(action);
      let signal = null;
      for (;;) {
        if (!soon) await delay(LABEL_POLL_MS);
        soon = false;
        if (!isTaskOnScreen(getState(), OPERATOR_FLOW.PACKING, orderId)) break;
        await pause(dispatch(fetchPackingOrder(orderId)));
        const { order } = getState().operatorPacking;
        if (!isTaskOnScreen(getState(), OPERATOR_FLOW.PACKING, orderId)) break;
        if (order?.id === orderId && !awaitingLabel(order)) {
          signal = courierWaitSignal(order);
          break;
        }
        if (Date.now() >= labelWait.until) {
          signal = courierTimeoutSignal(order);
          break;
        }
      }
      dispatch(courierWaitEnded(labelWait));
      if (signal) dispatch(signalRaised(signal));
    },
  });
};
